import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import "./budgets.css";

const VERSION_TYPES = [
  "original",
  "working",
  "approved",
  "revised",
  "forecast",
];

const emptyVersionForm = () => ({
  version_name: "",
  version_code: "",
  version_type: "working",
  notes: "",
});

const emptyLineForm = () => ({
  account_id: "",
  department_id: "",
  project_id: "",
  description: "",
  planning_method: "manual",
  notes: "",
  amount_total: 0,
});

function currency(n) {
  return `₦${Number(n || 0).toLocaleString()}`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

function monthShortLabel(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleString("en-US", { month: "short" });
}

function buildBudgetMatrixRows(budgetLines = [], budgetMonths = {}) {
  return budgetLines.map((line) => ({
    id: line.id,
    line,
    label: `${line.account?.account_number || ""} - ${
      line.account?.name || "Unknown"
    }`,
    subLabel: [
      line.department?.name || null,
      line.project?.name || null,
      line.planning_method || null,
    ]
      .filter(Boolean)
      .join(" · "),
    group: line.account?.type || "Other",
    category: line.account?.category || "Other",
    months: (budgetMonths[line.id] || []).map((m) => ({
      ...m,
      month_key: m.month_start,
      month_label: m.month_label,
      final_amount: Number(m.final_amount) || 0,
    })),
  }));
}

function AddBudgetLineModal({
  open,
  onClose,
  onSaved,
  companyId,
  sessionUserId,
  budgetVersionId,
  accounts,
  departments,
  projects,
}) {
  const [form, setForm] = useState(emptyLineForm());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm(emptyLineForm());
      setErr("");
      setSaving(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!form.account_id) {
      setErr("Please select an account.");
      return;
    }

    try {
      setSaving(true);

      const { data: row, error } = await supabase
        .from("budget_lines")
        .insert({
          company_id: companyId,
          budget_version_id: budgetVersionId,
          account_id: form.account_id,
          department_id: form.department_id || null,
          project_id: form.project_id || null,
          description: form.description.trim() || null,
          planning_method: form.planning_method,
          notes: form.notes.trim() || null,
          amount_total: Number(form.amount_total || 0),
          created_by: sessionUserId,
          updated_by: sessionUserId,
        })
        .select()
        .single();

      if (error) throw error;

      const { error: genErr } = await supabase.rpc(
        "generate_budget_line_months",
        {
          p_budget_line_id: row.id,
          p_replace_existing: true,
        }
      );

      if (genErr) throw genErr;

      if (Number(form.amount_total || 0) !== 0) {
        const { error: spreadErr } = await supabase.rpc(
          "spread_budget_line_evenly",
          {
            p_budget_line_id: row.id,
            p_total_amount: Number(form.amount_total || 0),
          }
        );

        if (spreadErr) throw spreadErr;
      }

      await onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Add budget line error:", error);
      setErr(error.message || "Failed to add budget line.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="kp-modal-overlay" onClick={onClose}>
      <div
        className="kp-modal kp-modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kp-modal-header">
          <div>
            <h3>Add Budget Line</h3>
            <p>Add a new account line to this budget version.</p>
          </div>
          <button
            type="button"
            className="kp-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="kp-form">
          <div className="kp-form-grid kp-form-grid-4">
            <div className="kp-field">
              <label>Account</label>
              <select
                value={form.account_id}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, account_id: e.target.value }))
                }
                required
              >
                <option value="">Select account</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.account_number} - {account.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="kp-field">
              <label>Department</label>
              <select
                value={form.department_id}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    department_id: e.target.value,
                  }))
                }
              >
                <option value="">None</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="kp-field">
              <label>Project</label>
              <select
                value={form.project_id}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, project_id: e.target.value }))
                }
              >
                <option value="">None</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="kp-field">
              <label>Planning Method</label>
              <select
                value={form.planning_method}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    planning_method: e.target.value,
                  }))
                }
              >
                <option value="manual">manual</option>
                <option value="driver">driver</option>
                <option value="formula">formula</option>
                <option value="copied_actual">copied_actual</option>
                <option value="forecast_seed">forecast_seed</option>
              </select>
            </div>
          </div>

          <div className="kp-form-grid">
            <div className="kp-field">
              <label>Description</label>
              <input
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Optional description"
              />
            </div>

            <div className="kp-field">
              <label>Initial Annual Total</label>
              <input
                type="number"
                value={form.amount_total}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, amount_total: e.target.value }))
                }
                placeholder="0"
              />
            </div>
          </div>

          <div className="kp-field">
            <label>Notes</label>
            <textarea
              rows="3"
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
            />
          </div>

          {err && <div className="kp-alert kp-alert-error">{err}</div>}

          <div className="kp-modal-footer">
            <button
              type="button"
              className="kp-btn kp-btn-soft"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="kp-btn kp-btn-primary"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Budget Line"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditBudgetLineModal({
  open,
  onClose,
  line,
  months,
  locked,
  onSaved,
  onDeleted,
}) {
  const [lineTotal, setLineTotal] = useState(0);
  const [monthValues, setMonthValues] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open && line) {
      setLineTotal(Number(line.amount_total) || 0);
      setMonthValues(
        (months || []).map((m) => ({
          id: m.id,
          month_label: m.month_label,
          month_start: m.month_start,
          final_amount: Number(m.final_amount) || 0,
        }))
      );
      setErr("");
      setSaving(false);
    }
  }, [open, line, months]);

  const saveMonthlyValues = async () => {
    setErr("");

    try {
      setSaving(true);

      for (const month of monthValues) {
        const { error } = await supabase
          .from("budget_line_months")
          .update({
            calc_amount: Number(month.final_amount || 0),
            override_amount: null,
            final_amount: Number(month.final_amount || 0),
            source_type: "manual",
          })
          .eq("id", month.id);

        if (error) throw error;
      }

      const total = monthValues.reduce(
        (sum, item) => sum + (Number(item.final_amount) || 0),
        0
      );

      const { error: lineErr } = await supabase
        .from("budget_lines")
        .update({
          amount_total: total,
        })
        .eq("id", line.id);

      if (lineErr) throw lineErr;

      await onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Save monthly values error:", error);
      setErr(error.message || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const spreadEvenly = async () => {
    setErr("");

    try {
      setSaving(true);

      const { error } = await supabase.rpc("spread_budget_line_evenly", {
        p_budget_line_id: line.id,
        p_total_amount: Number(lineTotal || 0),
      });

      if (error) throw error;

      await onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Spread evenly error:", error);
      setErr(error.message || "Failed to spread line.");
    } finally {
      setSaving(false);
    }
  };

  const deleteLine = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this budget line?"
    );
    if (!confirmed) return;

    setErr("");

    try {
      setSaving(true);

      const { error } = await supabase
        .from("budget_lines")
        .delete()
        .eq("id", line.id);
      if (error) throw error;

      await onDeleted?.();
      onClose?.();
    } catch (error) {
      console.error("Delete line error:", error);
      setErr(error.message || "Failed to delete budget line.");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !line) return null;

  return (
    <div className="kp-modal-overlay" onClick={onClose}>
      <div
        className="kp-modal kp-modal-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kp-modal-header">
          <div>
            <h3>
              {line.account?.account_number} - {line.account?.name}
            </h3>
            <p>
              {line.department?.name || "No department"} ·{" "}
              {line.project?.name || "No project"} · {line.planning_method}
            </p>
          </div>
          <button
            type="button"
            className="kp-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {locked ? (
          <div className="kp-note">
            This version is locked. Reopen the version before editing this line.
          </div>
        ) : (
          <>
            <div className="kp-total-editor">
              <label>Annual Total</label>
              <input
                type="number"
                value={lineTotal}
                onChange={(e) => setLineTotal(e.target.value)}
              />
              <button
                type="button"
                className="kp-btn kp-btn-soft"
                onClick={spreadEvenly}
                disabled={saving}
              >
                Spread Evenly
              </button>
              <button
                type="button"
                className="kp-btn kp-btn-danger"
                onClick={deleteLine}
                disabled={saving}
              >
                Delete Line
              </button>
            </div>

            <div className="kp-month-grid">
              {monthValues.map((month, index) => (
                <div key={month.id} className="kp-month-box">
                  <label>{month.month_label}</label>
                  <input
                    type="number"
                    value={month.final_amount}
                    onChange={(e) => {
                      const next = [...monthValues];
                      next[index] = {
                        ...next[index],
                        final_amount: e.target.value,
                      };
                      setMonthValues(next);
                    }}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {err && <div className="kp-alert kp-alert-error">{err}</div>}

        <div className="kp-modal-footer">
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={onClose}
            disabled={saving}
          >
            Close
          </button>
          {!locked && (
            <button
              type="button"
              className="kp-btn kp-btn-primary"
              onClick={saveMonthlyValues}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanningMatrixTable({
  title,
  rows = [],
  collapsedGroups = {},
  onToggleGroup,
  onRowClick,
}) {
  const monthColumns = useMemo(() => {
    const months = Array.from(
      new Set(
        rows.flatMap((r) =>
          Array.isArray(r.months) ? r.months.map((m) => m.month_key) : []
        )
      )
    ).sort();

    return months.map((m) => ({
      key: m,
      label: monthShortLabel(m),
    }));
  }, [rows]);

  const grouped = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      const key = row.group || "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });

    return Array.from(map.entries()).map(([group, items]) => ({
      group,
      rows: items,
    }));
  }, [rows]);

  return (
    <div className="kp-card">
      <div className="kp-card-head">
        <h3>{title}</h3>
      </div>

      <div className="kp-table-wrap kp-planning-matrix-wrap">
        <table className="kp-table kp-planning-matrix">
          <thead>
            <tr>
              <th className="sticky-col first-col sticky-head">Line Item</th>
              {monthColumns.map((col) => (
                <th key={col.key} className="sticky-head">
                  {col.label}
                </th>
              ))}
              <th className="sticky-head">Year</th>
            </tr>
          </thead>

          <tbody>
            {grouped.length === 0 ? (
              <tr>
                <td colSpan={monthColumns.length + 2} className="kp-empty-cell">
                  No budget lines yet.
                </td>
              </tr>
            ) : (
              grouped.map((section) => {
                const isCollapsed = !!collapsedGroups[section.group];

                const sectionTotals = {};
                monthColumns.forEach((col) => {
                  sectionTotals[col.key] = 0;
                });

                let sectionYear = 0;

                section.rows.forEach((row) => {
                  monthColumns.forEach((col) => {
                    const found = (row.months || []).find(
                      (m) => m.month_key === col.key
                    );
                    sectionTotals[col.key] += Number(found?.final_amount || 0);
                  });

                  sectionYear += (row.months || []).reduce(
                    (sum, m) => sum + (Number(m.final_amount) || 0),
                    0
                  );
                });

                return (
                  <React.Fragment key={section.group}>
                    <tr
                      className="kp-section-row kp-section-clickable"
                      onClick={() => onToggleGroup?.(section.group)}
                    >
                      <td
                        colSpan={monthColumns.length + 2}
                        className="sticky-col first-col"
                      >
                        <span className="kp-section-toggle">
                          {isCollapsed ? "▸" : "▾"}
                        </span>{" "}
                        {section.group}
                      </td>
                    </tr>

                    {!isCollapsed &&
                      section.rows.map((row) => {
                        const yearlyTotal = (row.months || []).reduce(
                          (sum, m) => sum + (Number(m.final_amount) || 0),
                          0
                        );

                        return (
                          <tr
                            key={row.id}
                            className="kp-clickable-row"
                            onClick={() => onRowClick?.(row)}
                          >
                            <td className="sticky-col first-col">
                              <div className="kp-line-main">
                                <strong>{row.label}</strong>
                                {row.subLabel ? (
                                  <small>{row.subLabel}</small>
                                ) : null}
                              </div>
                            </td>

                            {monthColumns.map((col) => {
                              const found = (row.months || []).find(
                                (m) => m.month_key === col.key
                              );

                              return (
                                <td key={col.key}>
                                  {currency(found?.final_amount || 0)}
                                </td>
                              );
                            })}

                            <td>{currency(yearlyTotal)}</td>
                          </tr>
                        );
                      })}

                    {!isCollapsed && (
                      <tr className="kp-subtotal-row">
                        <td className="sticky-col first-col">Subtotal</td>
                        {monthColumns.map((col) => (
                          <td key={col.key}>
                            {currency(sectionTotals[col.key])}
                          </td>
                        ))}
                        <td>{currency(sectionYear)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function BudgetWorkspace() {
  const navigate = useNavigate();
  const { id: budgetId } = useParams();
  const { companyId } = useCompany();

  const [sessionUserId, setSessionUserId] = useState("");
  const [role, setRole] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  const [budget, setBudget] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [selectedVersion, setSelectedVersion] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);

  const [budgetLines, setBudgetLines] = useState([]);
  const [budgetMonths, setBudgetMonths] = useState({});

  const [versionForm, setVersionForm] = useState(emptyVersionForm());

  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const matrixRows = useMemo(
    () => buildBudgetMatrixRows(budgetLines, budgetMonths),
    [budgetLines, budgetMonths]
  );

  const loadUserContext = useCallback(async () => {
    if (!companyId) return;

    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();

    if (authErr) throw authErr;
    if (!user?.id) throw new Error("User session not found.");

    setSessionUserId(user.id);

    const { data: member, error: memberErr } = await supabase
      .from("company_members")
      .select("role, accepted")
      .eq("company_id", companyId)
      .eq("user_id", user.id)
      .maybeSingle();

    if (memberErr) throw memberErr;

    const currentRole = String(member?.role || "member").toLowerCase();
    setRole(currentRole);
    setIsAdmin(["admin", "owner"].includes(currentRole));
  }, [companyId]);

  const loadDimensions = useCallback(async () => {
    if (!companyId) return;

    const [
      { data: accountRows, error: accountErr },
      { data: depRows, error: depErr },
      { data: projectRows, error: projectErr },
    ] = await Promise.all([
      supabase.rpc("get_budget_eligible_accounts", {
        p_company_id: companyId,
      }),
      supabase
        .from("departments")
        .select("*")
        .eq("company_id", companyId)
        .order("name", { ascending: true }),
      supabase
        .from("projects")
        .select("*")
        .eq("company_id", companyId)
        .order("name", { ascending: true }),
    ]);

    if (accountErr) throw accountErr;
    if (depErr) throw depErr;
    if (projectErr) throw projectErr;

    setAccounts(accountRows || []);
    setDepartments(depRows || []);
    setProjects(projectRows || []);
  }, [companyId]);

  const loadBudget = useCallback(async () => {
    if (!budgetId) return;

    const { data, error } = await supabase
      .from("budgets")
      .select("*")
      .eq("id", budgetId)
      .single();

    if (error) throw error;
    setBudget(data);
  }, [budgetId]);

  const loadVersions = useCallback(async () => {
    if (!budgetId) return;

    const { data, error } = await supabase
      .from("budget_versions")
      .select("*")
      .eq("budget_id", budgetId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    setVersions(data || []);
    const current = (data || []).find((v) => v.is_current) || data?.[0];
    setSelectedVersionId((prev) => prev || current?.id || "");
  }, [budgetId]);

  const loadSelectedVersion = useCallback(async () => {
    if (!selectedVersionId) {
      setSelectedVersion(null);
      setBudgetLines([]);
      setBudgetMonths({});
      return;
    }

    const { data: versionRow, error: versionErr } = await supabase
      .from("budget_versions")
      .select("*")
      .eq("id", selectedVersionId)
      .single();

    if (versionErr) throw versionErr;
    setSelectedVersion(versionRow);

    const { data: lines, error: linesErr } = await supabase
      .from("budget_lines")
      .select(
        `
        *,
        account:chart_of_accounts(id, account_number, name, type, category),
        department:departments(id, name, code),
        project:projects(id, name, code)
      `
      )
      .eq("budget_version_id", selectedVersionId)
      .order("created_at", { ascending: true });

    if (linesErr) throw linesErr;

    setBudgetLines(lines || []);

    if (!lines?.length) {
      setBudgetMonths({});
      return;
    }

    const lineIds = lines.map((l) => l.id);

    const { data: months, error: monthsErr } = await supabase
      .from("budget_line_months")
      .select("*")
      .in("budget_line_id", lineIds)
      .order("month_start", { ascending: true });

    if (monthsErr) throw monthsErr;

    const grouped = {};
    (months || []).forEach((m) => {
      if (!grouped[m.budget_line_id]) grouped[m.budget_line_id] = [];
      grouped[m.budget_line_id].push(m);
    });

    setBudgetMonths(grouped);
  }, [selectedVersionId]);

  const initialize = useCallback(async () => {
    if (!companyId || !budgetId) return;

    try {
      setLoading(true);
      setErr("");

      await Promise.all([
        loadUserContext(),
        loadBudget(),
        loadVersions(),
        loadDimensions(),
      ]);
    } catch (error) {
      console.error("Budget workspace load error:", error);
      setErr(error.message || "Failed to load budget workspace.");
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    budgetId,
    loadBudget,
    loadDimensions,
    loadUserContext,
    loadVersions,
  ]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (!selectedVersionId) return;

    loadSelectedVersion().catch((error) => {
      console.error("Load selected version error:", error);
      setErr(error.message || "Failed to load budget version details.");
    });
  }, [selectedVersionId, loadSelectedVersion]);

  const refreshVersionDetails = async () => {
    setErr("");
    await loadSelectedVersion();
  };

  const createVersion = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");

    if (!budgetId || !companyId || !sessionUserId) return;

    try {
      setSaving(true);

      const { data, error } = await supabase
        .from("budget_versions")
        .insert({
          company_id: companyId,
          budget_id: budgetId,
          version_name: versionForm.version_name.trim(),
          version_code: versionForm.version_code.trim() || null,
          version_type: versionForm.version_type,
          status: "draft",
          is_current: false,
          is_locked: false,
          notes: versionForm.notes.trim() || null,
          created_by: sessionUserId,
          updated_by: sessionUserId,
        })
        .select()
        .single();

      if (error) throw error;

      setVersionForm(emptyVersionForm());
      setSuccess("Version created successfully.");
      await loadVersions();
      setSelectedVersionId(data.id);
    } catch (error) {
      console.error("Create version error:", error);
      setErr(error.message || "Failed to create version.");
    } finally {
      setSaving(false);
    }
  };

  const approveVersion = async (versionId) => {
    setErr("");
    setSuccess("");

    try {
      setSaving(true);

      const { error } = await supabase.rpc("approve_budget_version", {
        p_budget_version_id: versionId,
        p_reason: "Approved from budget workspace",
      });

      if (error) throw error;

      setSuccess("Budget version approved successfully.");
      await loadVersions();
      await refreshVersionDetails();
    } catch (error) {
      console.error("Approve version error:", error);
      setErr(error.message || "Failed to approve version.");
    } finally {
      setSaving(false);
    }
  };

  const reopenVersion = async (versionId) => {
    setErr("");
    setSuccess("");

    try {
      setSaving(true);

      const { error } = await supabase.rpc("reopen_budget_version", {
        p_budget_version_id: versionId,
        p_reason: "Reopened from budget workspace",
      });

      if (error) throw error;

      setSuccess("Budget version reopened successfully.");
      await loadVersions();
      await refreshVersionDetails();
    } catch (error) {
      console.error("Reopen version error:", error);
      setErr(error.message || "Failed to reopen version.");
    } finally {
      setSaving(false);
    }
  };

  const cloneVersion = async () => {
    setErr("");
    setSuccess("");

    if (!selectedVersion) return;

    try {
      setSaving(true);

      const { data, error } = await supabase.rpc("clone_budget_version", {
        p_source_budget_version_id: selectedVersion.id,
        p_new_version_name: `${selectedVersion.version_name} Copy`,
        p_new_version_code: null,
        p_new_version_type: "working",
        p_notes: `Cloned from ${selectedVersion.version_name}`,
      });

      if (error) throw error;

      const row = Array.isArray(data) ? data[0] : data;

      setSuccess("Version cloned successfully.");
      await loadVersions();

      if (row?.id) {
        setSelectedVersionId(row.id);
      }
    } catch (error) {
      console.error("Clone version error:", error);
      setErr(error.message || "Failed to clone version.");
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (groupName) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const exportToExcel = () => {
    const monthKeys = Array.from(
      new Set(
        matrixRows.flatMap((row) => (row.months || []).map((m) => m.month_key))
      )
    ).sort();

    const rows = matrixRows.map((row) => {
      const item = {
        Line_Item: row.label,
        Details: row.subLabel || "",
        Group: row.group,
      };

      monthKeys.forEach((monthKey) => {
        const found = (row.months || []).find((m) => m.month_key === monthKey);
        item[monthShortLabel(monthKey)] = Number(found?.final_amount || 0);
      });

      item.Year = (row.months || []).reduce(
        (sum, m) => sum + (Number(m.final_amount) || 0),
        0
      );

      return item;
    });

    const sheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Budget Matrix");
    XLSX.writeFile(
      workbook,
      `${(budget?.name || "budget").replace(/[^a-z0-9]/gi, "_")}_matrix.xlsx`
    );
  };

  const exportToPDF = () => {
    const doc = new jsPDF("l", "mm", "a4");

    const monthKeys = Array.from(
      new Set(
        matrixRows.flatMap((row) => (row.months || []).map((m) => m.month_key))
      )
    ).sort();

    const head = [
      [
        "Line Item",
        ...monthKeys.map((monthKey) => monthShortLabel(monthKey)),
        "Year",
      ],
    ];

    const body = matrixRows.map((row) => {
      const yearTotal = (row.months || []).reduce(
        (sum, m) => sum + (Number(m.final_amount) || 0),
        0
      );

      return [
        row.label,
        ...monthKeys.map((monthKey) => {
          const found = (row.months || []).find(
            (m) => m.month_key === monthKey
          );
          return Number(found?.final_amount || 0).toLocaleString();
        }),
        yearTotal.toLocaleString(),
      ];
    });

    doc.setFontSize(14);
    doc.text(budget?.name || "Budget Matrix", 14, 14);

    autoTable(doc, {
      head,
      body,
      startY: 20,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [243, 244, 246], textColor: [17, 24, 39] },
      margin: { left: 8, right: 8 },
    });

    doc.save(
      `${(budget?.name || "budget").replace(/[^a-z0-9]/gi, "_")}_matrix.pdf`
    );
  };

  if (loading) {
    return (
      <div className="kp-budget-page">
        <div className="kp-budget-loading">Loading budget workspace…</div>
      </div>
    );
  }

  const editingMonths =
    editingRow && editingRow.line ? budgetMonths[editingRow.line.id] || [] : [];

  return (
    <div className="kp-budget-page">
      <div className="kp-budget-topbar">
        <div>
          <button
            type="button"
            className="kp-btn kp-btn-soft kp-btn-back"
            onClick={() => navigate("/budgets")}
          >
            ← Back to Budgets
          </button>

          <h2>{budget?.name || "Budget Workspace"}</h2>
          <p>
            {budget?.budget_type || "budget"} · FY {budget?.fiscal_year || "—"}{" "}
            · {formatDate(budget?.start_date)} – {formatDate(budget?.end_date)}{" "}
            · Role: <strong>{role || "member"}</strong>
          </p>
        </div>

        <div className="kp-inline-actions">
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={exportToExcel}
          >
            Export Excel
          </button>
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={exportToPDF}
          >
            Export PDF
          </button>
          <button
            type="button"
            className="kp-btn kp-btn-primary"
            onClick={() => setShowAddLineModal(true)}
            disabled={!selectedVersionId || selectedVersion?.is_locked}
          >
            Add Budget Line
          </button>
        </div>
      </div>

      {err && <div className="kp-alert kp-alert-error">{err}</div>}
      {success && <div className="kp-alert kp-alert-success">{success}</div>}

      <div className="kp-grid-2">
        <div className="kp-card">
          <div className="kp-card-head">
            <h3>Version Management</h3>
          </div>

          <form onSubmit={createVersion} className="kp-form">
            <div className="kp-form-grid">
              <div className="kp-field">
                <label>Version Name</label>
                <input
                  value={versionForm.version_name}
                  onChange={(e) =>
                    setVersionForm((prev) => ({
                      ...prev,
                      version_name: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div className="kp-field">
                <label>Version Code</label>
                <input
                  value={versionForm.version_code}
                  onChange={(e) =>
                    setVersionForm((prev) => ({
                      ...prev,
                      version_code: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="kp-field">
                <label>Version Type</label>
                <select
                  value={versionForm.version_type}
                  onChange={(e) =>
                    setVersionForm((prev) => ({
                      ...prev,
                      version_type: e.target.value,
                    }))
                  }
                >
                  {VERSION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="kp-field">
              <label>Notes</label>
              <textarea
                rows="2"
                value={versionForm.notes}
                onChange={(e) =>
                  setVersionForm((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
              />
            </div>

            <div className="kp-inline-actions">
              <button className="kp-btn kp-btn-primary" disabled={saving}>
                Add Version
              </button>
              {!!selectedVersionId && (
                <button
                  type="button"
                  className="kp-btn kp-btn-soft"
                  onClick={cloneVersion}
                  disabled={saving}
                >
                  Clone Selected Version
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="kp-card">
          <div className="kp-card-head">
            <h3>Versions</h3>
          </div>

          <div className="kp-version-list">
            {versions.length === 0 ? (
              <div className="kp-empty">No versions available.</div>
            ) : (
              versions.map((version) => (
                <div
                  key={version.id}
                  className={`kp-version-item ${
                    selectedVersionId === version.id ? "selected" : ""
                  }`}
                >
                  <button
                    className="kp-version-main"
                    onClick={() => setSelectedVersionId(version.id)}
                  >
                    <div>
                      <strong>{version.version_name}</strong>
                      <div className="kp-version-meta">
                        {version.version_type} · {version.status}
                        {version.is_current ? " · current" : ""}
                        {version.is_locked ? " · locked" : ""}
                      </div>
                    </div>
                  </button>

                  <div className="kp-version-actions">
                    {isAdmin && !version.is_locked && (
                      <button
                        className="kp-btn kp-btn-primary kp-btn-xs"
                        onClick={() => approveVersion(version.id)}
                      >
                        Approve
                      </button>
                    )}

                    {isAdmin && version.is_locked && (
                      <button
                        className="kp-btn kp-btn-soft kp-btn-xs"
                        onClick={() => reopenVersion(version.id)}
                      >
                        Reopen
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <PlanningMatrixTable
        title={
          selectedVersion
            ? `Budget Matrix · ${selectedVersion.version_name}`
            : "Budget Matrix"
        }
        rows={matrixRows}
        collapsedGroups={collapsedGroups}
        onToggleGroup={toggleGroup}
        onRowClick={(row) => setEditingRow(row)}
      />

      <div className="kp-note">
        Click any line item in the matrix to open the editor modal. Category
        headers can be collapsed and expanded for easier review.
      </div>

      <AddBudgetLineModal
        open={showAddLineModal}
        onClose={() => setShowAddLineModal(false)}
        onSaved={refreshVersionDetails}
        companyId={companyId}
        sessionUserId={sessionUserId}
        budgetVersionId={selectedVersionId}
        accounts={accounts}
        departments={departments}
        projects={projects}
      />

      <EditBudgetLineModal
        open={!!editingRow}
        onClose={() => setEditingRow(null)}
        line={editingRow?.line || null}
        months={editingMonths}
        locked={!!selectedVersion?.is_locked}
        onSaved={refreshVersionDetails}
        onDeleted={refreshVersionDetails}
      />
    </div>
  );
}
